import createDOMPurify from 'dompurify/dist/purify.js'

// 전역 훅을 변경하지 않도록 이 에디터 전용 sanitizer 인스턴스를 만든다.
const purifier = createDOMPurify(window)

export function safeUrl (value, kind = 'link') {
  // 이전 서버는 원본 파일명을 그대로 썼으므로 파일명 공백은 URL 인코딩해 보존한다.
  const url = String(value || '').trim().replace(/ /g, '%20')
  // URL 안의 제어문자는 스킴 우회 방지를 위해 거부한다.
  if (!url || Array.from(url).some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return ''
  if (kind === 'frame') {
    try {
      const parsed = new URL(url, window.location.href)
      return parsed.protocol === 'https:' ? parsed.href : ''
    } catch (error) { return '' }
  }
  // 저장된 상대 이미지 경로와 책갈피 링크도 기존 서버에서 그대로 해석하게 한다.
  if (/^(https?:\/\/|\/\/|\/|\.\.?\/|#)/i.test(url)) return url
  if (kind === 'link' && /^(mailto:|tel:)/i.test(url)) return url
  return /^[^:/?#]+(?:[/?#]|$)/.test(url) && !/^[^/?#]*:/.test(url) ? url : ''
}

export function cleanStyle (value) {
  const element = document.createElement('span')
  element.style.cssText = value || ''
  Array.from(element.style).forEach(property => {
    const setting = element.style.getPropertyValue(property)
    // 위치 고정, 외부 CSS 로딩, CSS 실행 표현식은 본문에 필요하지 않다.
    if (!/^(color|background-color|font-.+|text-.+|line-height|letter-spacing|word-spacing|white-space|vertical-align|border(?:-.+)?|padding(?:-.+)?|margin(?:-.+)?|(?:min-|max-)?width|(?:min-|max-)?height|list-style-type|list-style-position|table-layout|border-collapse|border-spacing|float|clear|display)$/.test(property) || /url\s*\(|expression\s*\(|@import|javascript:/i.test(setting)) {
      element.style.removeProperty(property)
    }
  })
  return element.style.cssText
}

purifier.addHook('afterSanitizeAttributes', node => {
  if (node.hasAttribute('style')) {
    const style = cleanStyle(node.getAttribute('style'))
    if (style) node.setAttribute('style', style)
    else node.removeAttribute('style')
  }
  const tag = node.nodeName.toLowerCase()
  if (tag === 'a') {
    if (node.hasAttribute('href') && !safeUrl(node.getAttribute('href'))) node.removeAttribute('href')
    if (node.getAttribute('target') === '_blank') node.setAttribute('rel', 'noopener noreferrer')
  }
  if (node.hasAttribute('src')) {
    const src = safeUrl(node.getAttribute('src'), tag === 'iframe' ? 'frame' : 'image')
    if (src) node.setAttribute('src', src)
    else node.removeAttribute('src')
  }
  if (tag === 'iframe') {
    // 기존 외부 임베드 제공자를 유지하되 srcdoc/상위 창 탐색 권한은 허용하지 않는다.
    // 같은 출처에는 allow-same-origin을 주지 않아 sandbox 탈출을 막는다.
    node.removeAttribute('srcdoc')
    const src = node.getAttribute('src')
    const sameOrigin = src && new URL(src, window.location.href).origin === window.location.origin
    node.setAttribute('sandbox', sameOrigin ? 'allow-scripts allow-presentation' : 'allow-scripts allow-same-origin allow-presentation')
    node.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin')
    node.setAttribute('allowfullscreen', '')
  }
})

export function mediaUrl (value) {
  // 기존 미디어 삽입과 같이 일반 공유 주소도 플레이어 주소로 바꿔 준다.
  try {
    const url = new URL(safeUrl(value), window.location.href)
    const youtube = /^(www\.|m\.)?youtube\.com$/.test(url.hostname)
    const videoId = url.hostname === 'youtu.be' ? url.pathname.slice(1) : youtube && url.pathname === '/watch' ? url.searchParams.get('v') : youtube && /^\/(shorts|live)\//.test(url.pathname) ? url.pathname.split('/')[2] : ''
    if (videoId && /^[\w-]+$/.test(videoId)) return 'https://www.youtube.com/embed/' + videoId
    if (/^(www\.)?vimeo\.com$/.test(url.hostname) && /^\/\d+$/.test(url.pathname)) return 'https://player.vimeo.com/video' + url.pathname
    return safeUrl(value, 'frame')
  } catch (error) { return '' }
}

export function sanitizeHtml (html) {
  return purifier.sanitize(String(html || ''), {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allowfullscreen', 'frameborder', 'sandbox', 'referrerpolicy', 'target'],
    FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'object', 'embed'],
    FORBID_ATTR: ['srcdoc', 'srcset', 'autoplay', 'contenteditable']
  })
}

// TinyMCE가 남긴 data-mce-*는 UI 내부 정보이며 저장 본문에는 필요하지 않다.
export function htmlAttributes (element, excluded = []) {
  const attrs = {}
  Array.from(element.attributes).forEach(attribute => {
    if (!/^data-mce-|^on/i.test(attribute.name) && excluded.indexOf(attribute.name) === -1) {
      attrs[attribute.name] = attribute.value
    }
  })
  return attrs
}



// WEBPACK FOOTER //
// ./src/components/ETC/editor/html.js